// WF-POLISH (Andrei, 2026-10-27: "you don't see the responsibilities, what it does… and it has
// all these runs, hermes, content studio copy… I don't think we need all that"): what a DE is and
// does, at the top of its Overview. Role, goal and backstory (its operating profile, synced by
// `infra/ops/htva-profile-sync.ts`, or typed here), its seat's responsibilities, its KPIs this
// week, the tasks assigned to its seat that are still open, and its last five runs in plain
// words. Runtime details (template, deploy, Hermes container, AiGentPanel profile, keys) sit in
// one "Runtime" line at the bottom, behind an (i). Managers edit the profile (the pencil, visible
// on hover and focus). Empty parts say so; nothing is invented.

import { profileGapText } from "@companyos/schema/contracts";
import { ApiError } from "@companyos/sdk";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  Textarea,
  toast,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { PencilIcon } from "lucide-react";
import { type FormEvent, type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { DeProfile } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import {
  isLive,
  RUN_STATE_LABELS,
  UsageCost,
  useCostCurrency,
  wakeupSource,
} from "../agents/agent-parts";
import { runsQuery } from "../agents/agent-queries";
import { useWalletOn } from "../billing/use-wallet-on";
import { type CompanyAccess, isManager } from "../company";
import { RuntimeSyncChip } from "../control/runtime-sync-chip";
import { formatCardMetric, formatDateTime } from "../format";
import { keys, orgKeys, seatScorecardQuery } from "../queries";
import { LoadError, usePeriod } from "../screen-parts";
import { useFeature } from "../use-feature";
import { DeRules } from "./de-rules";
import { problemText } from "./de-status";
import { HowIWork } from "./how-i-work";
import { ProfileDefaultDiff } from "./profile-default";

const RECENT = 5;
const OPEN_TASKS = 8;
/** At most this many pages are read for a list with no server-side filter for what we show. */
const MAX_PAGES = 10;

/** Every page of a cursor list, up to `MAX_PAGES`; `complete` says whether the end was reached. */
export async function allPages<T>(
  fetch: (cursor: string | undefined) => Promise<{ data: T[]; next_cursor: string | null }>,
): Promise<{ rows: T[]; complete: boolean }> {
  const rows: T[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < MAX_PAGES; i++) {
    const p = await fetch(cursor);
    rows.push(...p.data);
    if (!p.next_cursor) return { rows, complete: true };
    cursor = p.next_cursor;
  }
  return { rows, complete: false };
}

/** A small caption over each part (no cards: the page is the DE). */
export function Part({
  title,
  info,
  actions,
  children,
  slot,
}: {
  title: string;
  info?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  slot: string;
}) {
  return (
    <section className="group/part flex min-w-0 flex-col gap-2" data-slot={slot} aria-label={title}>
      <h3 className="flex items-center gap-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        {title}
        {info ? <InfoTip label={`About ${title.toLowerCase()}`}>{info}</InfoTip> : null}
        {actions ? <span className="ml-auto inline-flex items-center gap-1">{actions}</span> : null}
      </h3>
      {children}
    </section>
  );
}

export const Empty = ({ children }: { children: ReactNode }) => (
  <p className="text-sm text-muted-foreground">{children}</p>
);

/** Long text shown to three lines, "More" opens the rest. */
export function Expandable({ text, slot }: { text: string; slot: string }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 280 || text.split("\n").length > 3;
  return (
    <div className="flex min-w-0 flex-col items-start gap-1" data-slot={slot}>
      <p
        className={`text-sm whitespace-pre-line text-foreground ${open || !long ? "" : "line-clamp-3"}`}
      >
        {text}
      </p>
      {long ? (
        <Button
          variant="link"
          size="sm"
          className="h-auto px-0"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? "Less" : "More"}
        </Button>
      ) : null}
    </div>
  );
}

/** Role, goal and backstory, editable by managers (the pencil). */
export function ProfileFields({
  role,
  goal,
  backstory,
  onEdit,
  editLabel,
}: {
  role: string | null;
  goal: string | null;
  backstory: string | null;
  onEdit?: (() => void) | undefined;
  editLabel: string;
}) {
  const edit = onEdit ? (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label={editLabel}
      className="opacity-0 group-hover/part:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
      onClick={onEdit}
      data-slot="edit-profile"
    >
      <PencilIcon aria-hidden="true" />
    </Button>
  ) : null;
  return (
    <div className="grid min-w-0 gap-6 lg:grid-cols-2">
      <Part title="Role" slot="profile-role" actions={edit}>
        {role ? (
          <p className="text-sm text-foreground">{role}</p>
        ) : (
          <Empty>No role written yet.</Empty>
        )}
      </Part>
      <Part title="Goal" slot="profile-goal">
        {goal ? <p className="text-sm text-foreground">{goal}</p> : <Empty>No goal yet.</Empty>}
      </Part>
      {backstory ? (
        <div className="lg:col-span-2">
          <Part title="Backstory" slot="profile-backstory" info="How it thinks and works.">
            <Expandable text={backstory} slot="backstory-text" />
          </Part>
        </div>
      ) : null}
    </div>
  );
}

export function DeAbout({
  api,
  company,
  de,
  kpi,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
  kpi: boolean;
}) {
  const manager = isManager(company);
  const [editing, setEditing] = useState(false);
  // RECALL-RULES: the rules part is the `memory` add-on's
  const memory = useFeature("memory").enabled;
  return (
    <div className="flex min-w-0 flex-col gap-6" data-slot="de-about">
      {editing ? (
        <DeProfileDialog api={api} company={company} de={de} onClose={() => setEditing(false)} />
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <RuntimeSyncChip api={api} co={company.id} subject={{ kind: "de", id: de.membership_id }} />
        <ProfileDefaultDiff api={api} company={company} de={de} />
      </div>
      <ProfileFields
        role={de.role}
        goal={de.goal}
        backstory={de.backstory}
        onEdit={manager ? () => setEditing(true) : undefined}
        editLabel={`Edit what ${workerName(de.display_name)} is`}
      />
      <HowIWork api={api} company={company} de={de} />
      {memory ? <DeRules api={api} company={company} de={de} /> : null}
      <div className="grid min-w-0 gap-6 lg:grid-cols-2">
        <Responsibilities api={api} company={company} de={de} />
        {kpi ? <Kpis api={api} company={company} de={de} /> : null}
      </div>
      <div className="grid min-w-0 gap-6 lg:grid-cols-2">
        <OpenTasks api={api} company={company} de={de} />
        <RecentWork api={api} company={company} de={de} />
      </div>
    </div>
  );
}

function Responsibilities({
  api,
  company,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
}) {
  // Codex WF-POLISH r1 #4: every page (archived ones come back too), so an active one on a
  // later page is never missed.
  const seat = de.seat?.id;
  const q = useQuery({
    queryKey: [...orgKeys.responsibilities(company.id, seat ?? ""), "all"],
    queryFn: () =>
      allPages((cursor) =>
        api.client.responsibilities.list({
          params: { co: company.id, id: seat as string },
          query: { limit: 200, ...(cursor ? { cursor } : {}) },
        }),
      ),
    enabled: Boolean(seat),
  });
  const rows = (q.data?.rows ?? []).filter((r) => r.status === "active");
  return (
    <Part
      title="Responsibilities"
      slot="de-responsibilities"
      info="What its seat is accountable for."
    >
      {!de.seat ? (
        <Empty>No seat, so no responsibilities.</Empty>
      ) : q.isPending ? (
        <Empty>…</Empty>
      ) : q.isError ? (
        <LoadError what="Responsibilities" error={q.error} />
      ) : rows.length === 0 && q.data?.complete ? (
        <Empty>None on its seat yet.</Empty>
      ) : (
        // Codex WF-POLISH r2 #1: past the page cap the list says it goes on (never "none").
        <ul className="flex list-disc flex-col gap-1 pl-4 text-sm">
          {rows.map((r) => (
            <li key={r.id} title={r.description ?? undefined}>
              {r.name}
            </li>
          ))}
          {q.data?.complete ? null : (
            <li
              className="list-none text-xs text-muted-foreground"
              data-slot="de-responsibilities-more"
            >
              {rows.length === 0 ? "Too many on its seat to show here." : "and more"}
            </li>
          )}
        </ul>
      )}
    </Part>
  );
}

function Kpis({ api, company, de }: { api: Api; company: CompanyAccess; de: DeProfile }) {
  const week = usePeriod();
  const q = useQuery({
    ...seatScorecardQuery(api, company.id, de.seat?.id ?? "", week),
    enabled: Boolean(de.seat),
    retry: false,
  });
  const metrics = q.data?.metrics ?? [];
  return (
    <Part title="KPIs" slot="de-overview-kpis" info={`This week (${week}): actual / target.`}>
      {!de.seat ? (
        <Empty>No seat, so no KPIs.</Empty>
      ) : q.isPending ? (
        <Empty>…</Empty>
      ) : q.isError ? (
        // Codex WF-POLISH r1 #7: "not shown" only for a refusal; anything else is an error.
        q.error instanceof ApiError && (q.error.status === 403 || q.error.status === 404) ? (
          <Empty>Not shown to you.</Empty>
        ) : (
          <LoadError what="KPIs" error={q.error} />
        )
      ) : metrics.length === 0 ? (
        <Empty>No KPIs on its seat yet.</Empty>
      ) : (
        <ul className="flex flex-col gap-1 text-sm">
          {metrics.map((m) => (
            <li key={m.metric_def_id} className="flex min-w-0 items-baseline gap-2">
              <span className="min-w-0 truncate">{m.name}</span>
              <span className="ml-auto shrink-0 text-muted-foreground tabular-nums">
                {formatCardMetric(m.actual, m.unit)} / {formatCardMetric(m.target, m.unit)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Part>
  );
}

/** "in_progress" → "In progress". */
const statusText = (s: string) => {
  const t = s.replace(/[_-]+/g, " ").trim();
  return t ? t[0]?.toUpperCase() + t.slice(1) : s;
};

function OpenTasks({ api, company, de }: { api: Api; company: CompanyAccess; de: DeProfile }) {
  const seat = de.seat?.id;
  // Codex WF-POLISH r1 #3: the list has no "open" filter (done is per board), so every page is
  // read before saying "nothing" or counting the rest; past the cap the count says "more".
  const q = useQuery({
    queryKey: ["company", company.id, "work", "tasks", "by-seat", seat ?? ""],
    queryFn: () =>
      allPages((cursor) =>
        api.client.tasks.list({
          params: { co: company.id },
          query: { assignee_seat_id: seat as string, limit: 200, ...(cursor ? { cursor } : {}) },
        }),
      ),
    enabled: Boolean(seat),
  });
  const open = (q.data?.rows ?? []).filter((t) => !t.completed_at);
  const more = open.length - OPEN_TASKS;
  return (
    <Part title="Open tasks" slot="de-open-tasks" info="Tasks assigned to its seat, not done.">
      {!seat ? (
        <Empty>No seat, so no tasks.</Empty>
      ) : q.isPending ? (
        <Empty>…</Empty>
      ) : q.isError ? (
        <LoadError what="Tasks" error={q.error} />
      ) : open.length === 0 && q.data?.complete ? (
        <Empty>Nothing assigned right now.</Empty>
      ) : open.length === 0 ? (
        <Empty>Too many tasks on its seat to show here.</Empty>
      ) : (
        <ul className="flex flex-col gap-1 text-sm">
          {open.slice(0, OPEN_TASKS).map((t) => (
            <li key={t.id} className="flex min-w-0 items-baseline gap-2">
              <Link
                to="/c/$slug/work/t/$taskId"
                params={{ slug: company.slug, taskId: t.id }}
                search={keepFocus}
                className="min-w-0 truncate underline-offset-4 hover:underline"
              >
                {t.title}
              </Link>
              <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                {statusText(t.status)}
              </span>
            </li>
          ))}
          {more > 0 || !q.data?.complete ? (
            <li className="text-xs text-muted-foreground" data-slot="de-open-tasks-more">
              {q.data?.complete ? `and ${more} more` : "and more"}
            </li>
          ) : null}
        </ul>
      )}
    </Part>
  );
}

/** The run in plain words: its own summary's first line, else what woke it. */
export function runLine(r: {
  summary: string | null;
  trigger_kind: Parameters<typeof wakeupSource>[0];
  trigger_ref: Parameters<typeof wakeupSource>[1];
}): string {
  const first = r.summary?.split("\n").find((l) => l.trim());
  return first ? first.trim() : wakeupSource(r.trigger_kind, r.trigger_ref);
}

function RecentWork({ api, company, de }: { api: Api; company: CompanyAccess; de: DeProfile }) {
  const agents = useFeature("agents");
  const currency = useCostCurrency(api, company.id, agents.enabled);
  const walletOn = useWalletOn(api, company.id, agents.enabled);
  const q = useInfiniteQuery({
    ...runsQuery(api, company.id, { membership_id: de.membership_id }, RECENT),
    enabled: agents.enabled,
  });
  const runs = (q.data?.pages[0]?.data ?? []).slice(0, RECENT);
  return (
    <Part
      title="Recent work"
      slot="de-recent-work"
      actions={
        agents.enabled ? (
          <Link
            to="/c/$slug/agents/de/$id"
            params={{ slug: company.slug, id: de.membership_id }}
            search={{ tab: "runs" }}
            className="text-xs font-normal tracking-normal normal-case underline-offset-4 hover:underline"
          >
            All runs
          </Link>
        ) : null
      }
    >
      {!agents.enabled ? (
        <Empty>Runs show once the Agents add-on is on.</Empty>
      ) : q.isPending ? (
        <Empty>…</Empty>
      ) : q.isError ? (
        <LoadError what="Recent work" error={q.error} />
      ) : runs.length === 0 ? (
        <Empty>No runs yet.</Empty>
      ) : (
        <ul className="flex flex-col gap-1.5 text-sm">
          {runs.map((r) => (
            <li key={r.id} className="flex min-w-0 items-baseline gap-2" data-run={r.id}>
              <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                {formatDateTime(r.started_at ?? r.created_at)}
              </span>
              <span className="min-w-0 truncate" title={r.summary ?? undefined}>
                {runLine(r)}
              </span>
              <span className="ml-auto inline-flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                {RUN_STATE_LABELS[r.state]}
                <UsageCost
                  usage={r}
                  currency={currency}
                  hideTokens={walletOn}
                  live={isLive(r.state)}
                  className="max-sm:hidden"
                />
              </span>
            </li>
          ))}
        </ul>
      )}
    </Part>
  );
}

/**
 * The runtime in one line at the bottom of the Overview: where it runs in a few words, the rest
 * (template, started, deploy, AiGentPanel profile, LiteLLM key, managed runtime) behind the (i).
 */
export function RuntimeLine({
  items,
  summary,
}: {
  items: { label: string; value: ReactNode }[];
  summary: ReactNode;
}) {
  return (
    <p
      className="flex min-w-0 flex-wrap items-center gap-1.5 border-t border-border pt-3 text-xs text-muted-foreground"
      data-slot="de-runtime-line"
    >
      <span className="font-medium text-foreground">Runtime</span>
      <span className="min-w-0 truncate">{summary}</span>
      <InfoTip label="Runtime details">
        <dl className="flex flex-col gap-1.5" data-slot="de-runtime-details">
          {items.map((i) => (
            <div key={i.label} className="flex min-w-0 flex-col">
              <dt className="text-muted-foreground">{i.label}</dt>
              <dd className="min-w-0 break-words text-foreground">{i.value}</dd>
            </div>
          ))}
        </dl>
      </InfoTip>
    </p>
  );
}

/**
 * Setup › Role & standing orders: what the DE is (its operating profile) and the profile's
 * standing orders, read by anyone who opens Setup, edited by managers. The deployed template's
 * standing orders stay in Job & standing orders.
 */
export function DeProfileSection({
  api,
  company,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
}) {
  const manager = isManager(company);
  const [editing, setEditing] = useState(false);
  return (
    <div className="flex min-w-0 flex-col gap-6" data-slot="de-profile-section">
      {editing ? (
        <DeProfileDialog api={api} company={company} de={de} onClose={() => setEditing(false)} />
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <RuntimeSyncChip api={api} co={company.id} subject={{ kind: "de", id: de.membership_id }} />
        <ProfileDefaultDiff api={api} company={company} de={de} />
      </div>
      <ProfileFields
        role={de.role}
        goal={de.goal}
        backstory={de.backstory}
        onEdit={manager ? () => setEditing(true) : undefined}
        editLabel={`Edit what ${workerName(de.display_name)} is`}
      />
      <Part
        title="Standing orders"
        slot="profile-standing-orders"
        info="Rules it always follows: what it never does, and what needs a person's approval."
      >
        {de.standing_orders ? (
          <Expandable text={de.standing_orders} slot="standing-orders-text" />
        ) : (
          <Empty>None written yet.</Empty>
        )}
      </Part>
      {manager ? (
        <div>
          <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
            <PencilIcon aria-hidden="true" />
            Edit
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** Managers: role, goal, backstory and the profile's standing orders. */
export function DeProfileDialog({
  api,
  company,
  de,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  de: Pick<
    DeProfile,
    "membership_id" | "display_name" | "role" | "goal" | "backstory" | "standing_orders"
  >;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const save = useMutation({
    // A DE's responsibilities are its seat's (the dialog does not offer them here).
    mutationFn: ({ responsibilities: _, ...body }: ProfileDraft) =>
      api.client.digitalEmployees.update({
        params: { co: company.id, id: de.membership_id },
        body,
      }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: keys.de(company.id, de.membership_id) }),
        qc.invalidateQueries({ queryKey: keys.digitalEmployees(company.id) }),
      ]);
      toast.add({ title: "Saved", type: "success" });
      onClose();
    },
    onError: (e) => toast.add({ title: problemText(e, "Not saved."), type: "error" }),
  });
  return (
    <ProfileDialog
      title={`What ${workerName(de.display_name)} is`}
      initial={de}
      withStandingOrders
      pending={save.isPending}
      onSave={(d) => save.mutate(d)}
      onClose={onClose}
    />
  );
}

export type ProfileDraft = {
  /** CLOCKWORK B2: required, so a draft never clears them. */
  role?: string;
  goal?: string;
  backstory?: string;
  standing_orders?: string | null;
  /** An agent's own list (a DE's are its seat's). */
  responsibilities?: string[];
  /** HELPERS: an agent's skills, by key (chips on its Helpers card). */
  skills?: string[];
};

/** HELPERS: skill keys from "reel-script, hooks": trimmed, blanks and repeats dropped. */
export const skillsOf = (text: string) => [
  ...new Set(
    text
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean),
  ),
];

/** At most 50 skills of at most 120 characters each (the API's limits). */
export function skillsProblem(list: string[]): string | null {
  if (list.length > 50) return `At most 50 skills (this has ${list.length}).`;
  const long = list.find((s) => s.length > 120);
  return long ? `"${long.slice(0, 24)}…" is over 120 characters.` : null;
}

/** One responsibility per line; blank lines dropped. */
export const linesOf = (text: string) =>
  text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

/** At most 50 responsibilities of at most 500 characters each (the API's limits). */
export function dutiesProblem(list: string[]): string | null {
  if (list.length > 50) return `At most 50 responsibilities (this has ${list.length}).`;
  const long = list.findIndex((l) => l.length > 500);
  if (long >= 0) return `Line ${long + 1} is over 500 characters.`;
  return null;
}

/** The profile form (DE and AG): only what changed is sent. */
export function ProfileDialog({
  title,
  initial,
  withStandingOrders,
  withResponsibilities = false,
  withSkills = false,
  pending,
  onSave,
  onClose,
}: {
  title: string;
  initial: {
    role: string | null;
    goal: string | null;
    backstory: string | null;
    standing_orders?: string | null;
    responsibilities?: string[];
    skills?: string[];
  };
  withStandingOrders: boolean;
  withResponsibilities?: boolean;
  /** HELPERS: an agent's skills, comma-separated. */
  withSkills?: boolean;
  pending: boolean;
  onSave: (draft: ProfileDraft) => void;
  onClose: () => void;
}) {
  const id = useId();
  const [role, setRole] = useState(initial.role ?? "");
  const [goal, setGoal] = useState(initial.goal ?? "");
  const [backstory, setBackstory] = useState(initial.backstory ?? "");
  const [orders, setOrders] = useState(initial.standing_orders ?? "");
  const [duties, setDuties] = useState((initial.responsibilities ?? []).join("\n"));
  const [dutiesError, setDutiesError] = useState<string | null>(null);
  const [skills, setSkills] = useState((initial.skills ?? []).join(", "));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    // CLOCKWORK B2: role, goal and backstory are required of every DE and AG.
    const blanks = (["role", "goal", "backstory"] as const).filter(
      (k) => !{ role, goal, backstory }[k].trim(),
    );
    if (blanks.length) {
      setDutiesError(`${profileGapText(blanks)} can't be blank.`);
      return;
    }
    const next: ProfileDraft = {};
    const put = (k: "role" | "goal" | "backstory", v: string, was: string | null | undefined) => {
      const t = v.trim();
      if (t !== (was ?? null)) next[k] = t;
    };
    const putOrders = (v: string, was: string | null | undefined) => {
      const t = v.trim() || null;
      if (t !== (was ?? null)) next.standing_orders = t;
    };
    put("role", role, initial.role);
    put("goal", goal, initial.goal);
    put("backstory", backstory, initial.backstory);
    if (withStandingOrders) putOrders(orders, initial.standing_orders);
    if (withResponsibilities) {
      // Codex WF-POLISH r1 #8: over the limits says so and keeps the draft; nothing is cut.
      const list = linesOf(duties);
      const problem = dutiesProblem(list);
      setDutiesError(problem);
      if (problem) return;
      if (JSON.stringify(list) !== JSON.stringify(initial.responsibilities ?? []))
        next.responsibilities = list;
    }
    if (withSkills) {
      const list = skillsOf(skills);
      const problem = skillsProblem(list);
      setDutiesError(problem);
      if (problem) return;
      if (JSON.stringify(list) !== JSON.stringify(initial.skills ?? [])) next.skills = list;
    }
    if (Object.keys(next).length === 0) onClose();
    else onSave(next);
  };
  const field = (
    key: string,
    label: string,
    value: string,
    set: (v: string) => void,
    rows: number,
    max?: number,
  ) => (
    <Field>
      <FieldLabel htmlFor={`${id}-${key}`}>{label}</FieldLabel>
      <Textarea
        id={`${id}-${key}`}
        rows={rows}
        maxLength={max}
        value={value}
        onChange={(e) => set(e.target.value)}
      />
    </Field>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              Shown on its page. Role, goal and backstory are required.
            </DialogDescription>
          </DialogHeader>
          {field("role", "Role", role, setRole, 2, 2000)}
          {field("goal", "Goal", goal, setGoal, 2, 4000)}
          {field("backstory", "Backstory", backstory, setBackstory, 4, 8000)}
          {withResponsibilities
            ? // Codex WF-POLISH r2 #2: no length cap (a paste is never cut); dutiesProblem checks.
              field("duties", "Responsibilities (one per line)", duties, setDuties, 4)
            : null}
          {withSkills ? (
            <Field>
              <FieldLabel htmlFor={`${id}-skills`}>Skills (comma-separated)</FieldLabel>
              <Input
                id={`${id}-skills`}
                value={skills}
                placeholder="reel-script, hooks"
                onChange={(e) => setSkills(e.target.value)}
              />
            </Field>
          ) : null}
          {dutiesError ? (
            <p role="alert" className="text-sm text-destructive" data-slot="profile-duties-error">
              {dutiesError}
            </p>
          ) : null}
          {withStandingOrders
            ? field("orders", "Standing orders", orders, setOrders, 4, 20000)
            : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
